import React from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';

const FilterMenu = ({ label, children, count }) => {
  return (
    <details className="sp-filter-menu">
      <summary>
        <span>{label}</span>
        {count > 0 && <Badge variant="review">{count}</Badge>}
      </summary>
      <div className="sp-filter-menu__panel">{children}</div>
    </details>
  );
};

const FilterPill = ({ label, onRemove }) => {
  return (
    <span className="sp-filter-pill">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove filter ${label}`}>
        ×
      </button>
    </span>
  );
};

export default function InventoryFilterBar({
  search,
  onSearchChange,
  filters,
  typeOptions,
  statusOptions,
  conditionOptions,
  resultsCount,
  activeFilters,
  onToggleType,
  onToggleStatus,
  onAssignmentChange,
  onConditionChange,
  onDueChange,
  onClearFilters,
  onRemoveFilter
}) {
  return (
    <div className="sp-filter-bar" role="region" aria-label="Inventory filters">
      <div className="sp-filter-main">
        <div className="sp-filter-search">
          <label className="sp-label" htmlFor="inventory-search">Search</label>
          <input
            id="inventory-search"
            className="sp-input w-full"
            placeholder="Search by name, ID, tag, assigned person"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <div className="sp-filter-controls">
          <FilterMenu label="Type" count={filters.types.length}>
            {typeOptions.map((type) => (
              <label key={type} className="sp-filter-option">
                <input
                  type="checkbox"
                  checked={filters.types.includes(type)}
                  onChange={() => onToggleType(type)}
                />
                <span>{type}</span>
              </label>
            ))}
            {typeOptions.length === 0 && <span className="text-xs sp-muted">No types found.</span>}
          </FilterMenu>
          <FilterMenu label="Status" count={filters.statuses.length}>
            {statusOptions.map((status) => (
              <label key={status.value} className="sp-filter-option">
                <input
                  type="checkbox"
                  checked={filters.statuses.includes(status.value)}
                  onChange={() => onToggleStatus(status.value)}
                />
                <span>{status.label}</span>
              </label>
            ))}
          </FilterMenu>
          <FilterMenu label="Assignment" count={filters.assignment !== 'all' ? 1 : 0}>
            {['all', 'assigned', 'unassigned'].map((value) => (
              <label key={value} className="sp-filter-option">
                <input
                  type="radio"
                  name="assignmentFilter"
                  value={value}
                  checked={filters.assignment === value}
                  onChange={() => onAssignmentChange(value)}
                />
                <span>
                  {value === 'all' ? 'All' : value === 'assigned' ? 'Assigned' : 'Unassigned'}
                </span>
              </label>
            ))}
          </FilterMenu>
          <FilterMenu label="Condition" count={filters.condition !== 'all' ? 1 : 0}>
            <label className="sp-filter-option">
              <input
                type="radio"
                name="conditionFilter"
                value="all"
                checked={filters.condition === 'all'}
                onChange={() => onConditionChange('all')}
              />
              <span>Any</span>
            </label>
            {conditionOptions.map((condition) => (
              <label key={condition} className="sp-filter-option">
                <input
                  type="radio"
                  name="conditionFilter"
                  value={condition}
                  checked={filters.condition === condition}
                  onChange={() => onConditionChange(condition)}
                />
                <span>{condition}</span>
              </label>
            ))}
          </FilterMenu>
          <FilterMenu label="Due" count={filters.due !== 'any' ? 1 : 0}>
            {[
              { value: 'any', label: 'Any' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'dueSoon', label: 'Due soon (7 days)' }
            ].map((option) => (
              <label key={option.value} className="sp-filter-option">
                <input
                  type="radio"
                  name="dueFilter"
                  value={option.value}
                  checked={filters.due === option.value}
                  onChange={() => onDueChange(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </FilterMenu>
        </div>
        <div className="sp-filter-meta">
          <span className="text-sm sp-muted">{resultsCount} results</span>
          {activeFilters.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearFilters} type="button">
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="sp-filter-pills" aria-label="Active filters">
          {activeFilters.map((filter) => (
            <FilterPill key={filter.id} label={filter.label} onRemove={() => onRemoveFilter(filter)} />
          ))}
        </div>
      )}
    </div>
  );
}
